import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectDetailThemeStyles } from '../projectThemes';
import './ProjectDetailLayout.css';
import './NetflixProject.css';

const TableauEmbed: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = '';

    const vizEl = document.createElement('object');
    vizEl.className = 'tableauViz';
    vizEl.style.display = 'block';
    vizEl.style.width = '100%';
    vizEl.style.height = container.offsetWidth > 500
      ? container.offsetWidth * 0.75 + 'px'
      : '2177px';

    const params: Record<string, string> = {
      host_url: 'https%3A%2F%2Fpublic.tableau.com%2F',
      embed_code_version: '3',
      site_root: '',
      name: 'NetflixTrendDashboard/Netflix',
      tabs: 'no',
      toolbar: 'yes',
      animate_transition: 'yes',
      display_static_image: 'yes',
      display_spinner: 'yes',
      display_overlay: 'yes',
      display_count: 'yes',
      language: 'zh-CN',
      filter: 'publish=yes',
    };

    Object.entries(params).forEach(([name, value]) => {
      const param = document.createElement('param');
      param.name = name;
      param.value = value;
      vizEl.appendChild(param);
    });

    container.appendChild(vizEl);

    const script = document.createElement('script');
    script.src = 'https://public.tableau.com/javascripts/api/viz_v1.js';
    container.insertBefore(script, vizEl);

    return () => {
      container.innerHTML = '';
    };
  }, []);

  return <div ref={containerRef} className="netflix-project__tableau" />;
};

export const NetflixProject: React.FC = () => {
  const navigate = useNavigate();
  return (
  <main
    className="project-detail-page netflix-project-page"
    style={projectDetailThemeStyles.netflix}
  >
    <aside className="project-page__sidebar">
      <button type="button" className="project-page__back" onClick={() => navigate(-1)}>
        <span className="project-page__back-arrow" aria-hidden="true">
          &lt;-
        </span>
        back to showcase
      </button>
      <div className="project-page__sidebar-summary">
        <p className="project-page__eyebrow">data analysis / visual story</p>
        <h1
          className="project-page__title"
          aria-label="Netflix Content Distribution and Trend Analysis Dashboard"
        >
          Netflix Insights
        </h1>
        <p className="project-page__description">
          An interactive reading of content type, country distribution, ratings, genres, and
          release trends across the Netflix catalog.
        </p>
        <div className="project-page__tech-list" aria-label="Technologies used">
          <span>Data analysis</span>
          <span>Tableau</span>
          <span>Kaggle dataset</span>
        </div>
      </div>
    </aside>

    <section className="project-page__content" aria-label="Project content">
      <div className="project-page__section">
        <p className="project-page__section-kicker">analysis / dashboard</p>
        <h2 className="project-page__section-title">Reading the shape of a global catalog.</h2>
        <div className="project-page__section-body">
          <p>
            Built on the Kaggle Netflix Movies and TV Shows dataset (8,000+ records), this
            interactive Tableau dashboard provides a systematic analysis of Netflix's content
            strategy and growth trajectory.
          </p>
          <figure
            className="project-page__inline-figure netflix-project__interactive-figure"
            aria-label="Interactive Netflix Tableau dashboard"
          >
            <TableauEmbed />
          </figure>
          <p>
            Key dimensions explored include global content distribution by region, content type
            breakdown (Movies vs. TV Shows), rating categories, and year-over-year growth trends.
            The analysis reveals a significant content expansion between 2015 and 2019, with the
            United States, India, and the United Kingdom emerging as the primary content-producing
            markets, and movies accounting for approximately 70% of the platform's total library.
          </p>
          <p>
            The dashboard supports dynamic filtering by content type and title, enabling deeper
            exploration of the underlying data.
          </p>
        </div>
      </div>
    </section>
  </main>
  );
};
